import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useModalState, type OnboardingCountry } from '../hooks/useModalState';
import type {
  Product,
  OnboardingSystem,
  PlatformInfo,
  ColumnGroups,
} from '../pages/CatalogPage';

// Tab configuration
export const TABS = [
  { id: 'Products', label: 'Products' },
  { id: '3PI', label: 'Integrations' },
] as const;

// Define all the types we need
type ProductMatrixContextType = {
  // Navigation State
  selectedRegion: string;
  setSelectedRegion: React.Dispatch<React.SetStateAction<string>>;
  selectedCountry: string;
  setSelectedCountry: React.Dispatch<React.SetStateAction<string>>;
  selectedPlatform: string | null;
  setSelectedPlatform: React.Dispatch<React.SetStateAction<string | null>>;
  currentView: 'selection' | 'platform' | 'location' | 'onboarding';
  setCurrentView: React.Dispatch<
    React.SetStateAction<'selection' | 'platform' | 'location' | 'onboarding'>
  >;
  browseMode: boolean;
  setBrowseMode: React.Dispatch<React.SetStateAction<boolean>>;

  // Product Data
  platforms: PlatformInfo[];
  setPlatforms: React.Dispatch<React.SetStateAction<PlatformInfo[]>>;
  platformProducts: Product[];
  setPlatformProducts: React.Dispatch<React.SetStateAction<Product[]>>;
  lbm2Products: Product[];
  setLbm2Products: React.Dispatch<React.SetStateAction<Product[]>>;
  onboardingSystems: OnboardingSystem[];
  setOnboardingSystems: React.Dispatch<React.SetStateAction<OnboardingSystem[]>>;
  onboardingColumnGroups: ColumnGroups | null;
  setOnboardingColumnGroups: React.Dispatch<React.SetStateAction<ColumnGroups | null>>;

  // UI State
  platformLoading: boolean;
  setPlatformLoading: React.Dispatch<React.SetStateAction<boolean>>;
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  tabSearchQuery: string;
  setTabSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  highlightedProduct: string | null;
  setHighlightedProduct: React.Dispatch<React.SetStateAction<string | null>>;
  showScrollToTop: boolean;
  setShowScrollToTop: React.Dispatch<React.SetStateAction<boolean>>;
  expandedLevels: Set<string>;
  setExpandedLevels: React.Dispatch<React.SetStateAction<Set<string>>>;
  isAIDrawerOpen: boolean;
  setIsAIDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  toggleAIDrawer: () => void;
  aiDrawerWidth: number;
  setAIDrawerWidth: React.Dispatch<React.SetStateAction<number>>;
  isDisclaimerDismissed: boolean;
  setIsDisclaimerDismissed: React.Dispatch<React.SetStateAction<boolean>>;

  // Search State
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  searchResults: Product[];
  setSearchResults: React.Dispatch<React.SetStateAction<Product[]>>;
  isSearching: boolean;
  setIsSearching: React.Dispatch<React.SetStateAction<boolean>>;
  showSearchDropdown: boolean;
  setShowSearchDropdown: React.Dispatch<React.SetStateAction<boolean>>;

  // Options
  options: { countries: string[]; regions: string[] };
  setOptions: React.Dispatch<React.SetStateAction<{ countries: string[]; regions: string[] }>>;

  // Modal State (from useModalState hook)
  selectedDevice: Product | null;
  showDeviceModal: boolean;
  handleDeviceClick: (device: Product) => void;
  handleCloseDeviceModal: () => void;
  selectedPartnerProduct: Product | null;
  showPartnerModal: boolean;
  handlePartnerClick: (product: Product) => void;
  handleClosePartnerModal: () => void;
  selectedProduct: Product | null;
  showProductModal: boolean;
  handleProductClick: (product: Product) => void;
  handleCloseProductModal: () => void;
  selectedOnboardingCountry: OnboardingCountry | null;
  showOnboardingModal: boolean;
  handleOnboardingClick: (data: OnboardingCountry) => void;
  handleCloseOnboardingModal: () => void;
  setSelectedOnboardingCountry: React.Dispatch<React.SetStateAction<OnboardingCountry | null>>;
}

// Create the context with undefined as default (will be provided by Provider)
const ProductMatrixContext = createContext<ProductMatrixContextType | undefined>(undefined);

// Provider component
export function ProductMatrixProvider({ children }: { children: ReactNode }) {
  // Navigation State
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<
    'selection' | 'platform' | 'location' | 'onboarding'
  >('selection');
  const [browseMode, setBrowseMode] = useState<boolean>(false);

  // Product Data
  const [platforms, setPlatforms] = useState<PlatformInfo[]>([]);
  const [platformProducts, setPlatformProducts] = useState<Product[]>([]);
  const [lbm2Products, setLbm2Products] = useState<Product[]>([]);
  const [onboardingSystems, setOnboardingSystems] = useState<OnboardingSystem[]>([]);
  const [onboardingColumnGroups, setOnboardingColumnGroups] = useState<ColumnGroups | null>(null);

  // UI State
  const [platformLoading, setPlatformLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('Products');
  const [tabSearchQuery, setTabSearchQuery] = useState<string>('');
  const [highlightedProduct, setHighlightedProduct] = useState<string | null>(null);
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false);
  const [expandedLevels, setExpandedLevels] = useState<Set<string>>(new Set(['1', '2', '3']));
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState<boolean>(false);
  const [aiDrawerWidth, setAIDrawerWidth] = useState<number>(450);

  // Disclaimer dismissed state with localStorage persistence
  const [isDisclaimerDismissed, setIsDisclaimerDismissed] = useState<boolean>(() => {
    const saved = localStorage.getItem('disclaimer-dismissed');
    return saved === 'true';
  });

  // Sync disclaimer state to localStorage
  useEffect(() => {
    localStorage.setItem('disclaimer-dismissed', String(isDisclaimerDismissed));
  }, [isDisclaimerDismissed]);

  // AI Drawer toggle function
  const toggleAIDrawer = () => setIsAIDrawerOpen(!isAIDrawerOpen);

  // Search State
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState<boolean>(false);

  // Options
  const [options, setOptions] = useState<{ countries: string[]; regions: string[] }>({
    countries: [],
    regions: [],
  });

  // Modal State (using existing useModalState hook)
  const modalState = useModalState();

  // Combine all state into context value
  const value: ProductMatrixContextType = {
    // Navigation State
    selectedRegion,
    setSelectedRegion,
    selectedCountry,
    setSelectedCountry,
    selectedPlatform,
    setSelectedPlatform,
    currentView,
    setCurrentView,
    browseMode,
    setBrowseMode,

    // Product Data
    platforms,
    setPlatforms,
    platformProducts,
    setPlatformProducts,
    lbm2Products,
    setLbm2Products,
    onboardingSystems,
    setOnboardingSystems,
    onboardingColumnGroups,
    setOnboardingColumnGroups,

    // UI State
    platformLoading,
    setPlatformLoading,
    activeTab,
    setActiveTab,
    tabSearchQuery,
    setTabSearchQuery,
    highlightedProduct,
    setHighlightedProduct,
    showScrollToTop,
    setShowScrollToTop,
    expandedLevels,
    setExpandedLevels,
    isAIDrawerOpen,
    setIsAIDrawerOpen,
    toggleAIDrawer,
    aiDrawerWidth,
    setAIDrawerWidth,
    isDisclaimerDismissed,
    setIsDisclaimerDismissed,

    // Search State
    searchQuery,
    setSearchQuery,
    searchResults,
    setSearchResults,
    isSearching,
    setIsSearching,
    showSearchDropdown,
    setShowSearchDropdown,

    // Options
    options,
    setOptions,

    // Modal State
    ...modalState,
  };

  return <ProductMatrixContext.Provider value={value}>{children}</ProductMatrixContext.Provider>;
};

// Custom hook to use the context
export const useProductMatrix = () => {
  const context = useContext(ProductMatrixContext);
  if (context === undefined) {
    throw new Error('useProductMatrix must be used within a ProductMatrixProvider');
  }
  return context;
};
